import { isApiError } from './errors';

/**
 * Riding out load shedding in the SPA (R8).
 *
 * When the API sheds load it answers 503 with a `*_BUSY` code (or
 * `AUDIT_CONTENTION`) and says when to come back. A user action that hits it is
 * retried here, quietly, a few times, after a jittered wait so a crowd of
 * clients does not come back in lockstep. The budget is bounded twice: at most
 * `BUSY_MAX_RETRIES` retries, and no attempt starts more than
 * `BUSY_MAX_ELAPSED_MS` after the first one, so the person sees the still-busy
 * state instead of an endless spinner.
 *
 * No React and no i18n: the flows (login, start, submit) own the UI and learn
 * what is happening through `onAttempt` (a request is in flight) and `onRetry`
 * (waiting before the next one).
 */

export const BUSY_MAX_RETRIES = 3;
export const BUSY_MAX_WAIT_MS = 10_000;
export const BUSY_MAX_ELAPSED_MS = 25_000;

/** The retry-after default when the server named none. */
const DEFAULT_RETRY_AFTER_SECONDS = 2;

/**
 * A busy answer worth retrying: a 503 with a `*_BUSY` code or
 * `AUDIT_CONTENTION`. The allocation 409s (`ALLOCATION_BUSY`, `CATEGORY_FULL`)
 * are business answers, and 429 and 500 are never retried.
 */
export function isBusyRetryable(err: unknown): boolean {
  return isApiError(err) && err.status === 503 && (err.code.endsWith('_BUSY') || err.code === 'AUDIT_CONTENTION');
}

/**
 * The base wait (RA) in milliseconds: the envelope's `details.retryAfter`,
 * else the `Retry-After` header, else 2 s. Only a positive number counts.
 */
export function retryAfterMs(err: unknown): number {
  if (isApiError(err)) {
    const fromDetails = (err.details as { retryAfter?: unknown } | null | undefined)?.retryAfter;
    if (typeof fromDetails === 'number' && Number.isFinite(fromDetails) && fromDetails > 0) return fromDetails * 1000;
    if (err.retryAfterSeconds !== null && err.retryAfterSeconds > 0) return err.retryAfterSeconds * 1000;
  }
  return DEFAULT_RETRY_AFTER_SECONDS * 1000;
}

export interface BusyRetryState {
  /** Which retry is about to happen (1-based). */
  retry: number;
  maxRetries: number;
  /** How long the runner waits before it. */
  delayMs: number;
  /** The error that triggered it. */
  error: unknown;
}

export interface BusyRetryOptions {
  /** Aborts the wait between attempts and is handed to every attempt. */
  signal?: AbortSignal | undefined;
  /**
   * Which errors are retried; replaces `isBusyRetryable`. A flow that also
   * waits out its own codes (submit's held idempotency key) composes it:
   * `(e) => isBusyRetryable(e) || isHeldKey(e)`.
   */
  isRetryable?: (err: unknown) => boolean;
  /** Called before each attempt with its number (1-based). */
  onAttempt?: ((n: number) => void) | undefined;
  /** Called before each wait. */
  onRetry?: ((state: BusyRetryState) => void) | undefined;
  /** Injectable for tests. */
  random?: () => number;
  /** Injectable for tests. */
  now?: () => number;
}

/** Resolve after `ms`, or reject with the signal's reason when it aborts first. */
function sleep(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Run `attempt`, retrying busy answers within the budget (R8).
 *
 * Each wait is uniform in [RA, 2·RA], capped at `BUSY_MAX_WAIT_MS`. A retry is
 * not scheduled when it would start after `BUSY_MAX_ELAPSED_MS`; the last error
 * is rethrown instead. An attempt already running is never cut short by the
 * cap. An abort rejects with the signal's reason and starts nothing more.
 */
export async function runWithBusyRetry<T>(
  attempt: (signal?: AbortSignal) => Promise<T>,
  opts: BusyRetryOptions = {},
): Promise<T> {
  const { signal, isRetryable = isBusyRetryable, onAttempt, onRetry, random = Math.random, now = Date.now } = opts;
  const start = now();
  for (let n = 1; ; n += 1) {
    onAttempt?.(n);
    try {
      return await attempt(signal);
    } catch (err) {
      if (signal?.aborted) throw signal.reason;
      if (!isRetryable(err) || n > BUSY_MAX_RETRIES) throw err;
      const base = retryAfterMs(err);
      const delayMs = Math.min(BUSY_MAX_WAIT_MS, Math.round(base + random() * base));
      if (now() - start + delayMs > BUSY_MAX_ELAPSED_MS) throw err;
      onRetry?.({ retry: n, maxRetries: BUSY_MAX_RETRIES, delayMs, error: err });
      await sleep(delayMs, signal);
    }
  }
}
